import React, { useState, useEffect, useMemo } from 'react';
import { MTable } from 'ui/mtable/MTable';
import { withStyles } from '@material-ui/core/styles';
import { ReportTypeFilter, typeOptions } from './components/ReportTypeFilter';
import { CompanyFilter } from './components/CompanyFilter';
import { GroupByFilter, groupingOptions } from './components/GroupByFilter';
import { loadCompanies } from '../../Services';
import formatters from '../../../formatters';
import { useReports } from '../../hooks/useReports';

const getColumns = (displayColumns = []) => {
    return [
        {
            field: 'date',
            title: 'Date',
            defaultSort: 'desc',
            notFilter: true,
            searchable: false,
            render: rowData => formatters.onlydate(rowData.date),
        },
        {
            field: 'company',
            title: 'Company',
            notFilter: true,
        },
        {
            field: 'user_id',
            title: 'User',
            hidden: !displayColumns.includes('user'),
        },
        {
            field: 'site__name',
            title: 'Website',
            hidden: !displayColumns.includes('website'),
        },
        {
            field: 'logins',
            title: 'Login count',
        },
    ];
};

const styles = theme => ({
    menuItem: {
        display: 'flex',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    popper: {
        zIndex: 1500,
    },
    toolbar: {
        backgroundColor: '#e3f2fd',

        '& > div:nth-child(1)': {
            flex: '1 1 auto',
        },

        '& > div:nth-child(2)': {
            display: 'none',
        },
    },
});

const filterByCompanies = (logs = [], companies = []) => {
    if (companies.length === 0) {
        return logs;
    }
    const org_names = companies.map(company => company.business_name);
    return logs.filter(log => org_names.includes(log.company));
};

const UserActivityView = ({ classes, dateRange, app }) => {
    const { currentUser } = app;
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedType, setSelectedType] = useState(typeOptions[0]);
    const [selectedGroupBy, setSelectedGroupBy] = useState(groupingOptions[0]);
    const [companies, setCompanies] = useState([]);
    const [openFilter, setOpenFilter] = useState(null);

    const { data, isLoading, isValidating, refresh } = useReports(
        currentUser,
        selectedType.id,
        dateRange,
        selectedGroupBy.id
    );

    const logs = useMemo(() => {
        return Array.isArray(data) ? data : [];
    }, [data]);

    useEffect(() => {
        loadCompanies().then(data => {
            setCompanies(data);
            setSelectedCompanies(data);
        });
    }, []);

    const handleCompanyChange = (selectedCompanies, isAllSelected) => {
        setSelectedCompanies(selectedCompanies);
    };
    const handleTypeChange = type => {
        setSelectedType(type);
        if (type.id === 'logins_by_website') {
            setSelectedGroupBy({
                id: 'website',
                title: 'Website',
            });
        } else {
            setSelectedGroupBy({
                id: 'user',
                title: 'User',
            });
        }
    };
    const handleGroupChange = type => setSelectedGroupBy(type);

    const handleClickFilter = type =>
        setOpenFilter(prev => (prev !== type ? type : null));
    const handleCloseFilter = () => setOpenFilter(null);

    return (
        <MTable
            stateKey="UserActivity"
            columns={getColumns([selectedGroupBy.id])}
            data={filterByCompanies(logs, selectedCompanies)}
            loading={isLoading || isValidating}
            onRefresh={refresh}
            classes={{
                toolbar: classes.toolbar,
            }}
            primaryFilters={[
                () => (
                    <ReportTypeFilter
                        key="report_type"
                        onChange={handleTypeChange}
                        onClick={() => handleClickFilter('report_type')}
                        onClose={handleCloseFilter}
                        selected={selectedType}
                        open={openFilter === 'report_type'}
                    />
                ),
                () => (
                    <CompanyFilter
                        onChange={handleCompanyChange}
                        onClick={() => handleClickFilter('company')}
                        onClose={handleCloseFilter}
                        key="company"
                        companies={companies}
                        selected={selectedCompanies}
                        open={openFilter === 'company'}
                    />
                ),
                () => (
                    <GroupByFilter
                        onChange={handleGroupChange}
                        onClick={() => handleClickFilter('group')}
                        onClose={handleCloseFilter}
                        key="group"
                        selected={selectedGroupBy}
                        open={openFilter === 'group'}
                    />
                ),
            ]}
        />
    );
};

const UserActivity = withStyles(styles)(UserActivityView);

export { UserActivity };
